import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CaretDownIcon } from '@phosphor-icons/react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import {
  formatCurrency,
  assignCategoryColors,
  getCategoryColor,
  splitCategoryEmoji,
  stripCategoryEmoji,
} from '@budget/core';
import type {
  AssetCategory,
  AssetItem,
  AssetsData,
  Investment,
  InvestmentCategory,
  InvestmentsData,
  Tri,
} from '@budget/core';
import { useDashboard } from '../data/useDashboard';
import { type Scope } from '../components/ScopeToggle';
import { useScope } from '../hooks/useScope';
import { PersonBreakdown } from '../components/PersonBreakdown';
import { BreakdownLegend } from '../components/BreakdownLegend';
import { BreakdownLegendGrid } from '../components/BreakdownLegendGrid';
import { DonutCenterEmoji } from '../components/DonutCenterEmoji';
import { CardHeader } from '../components/CardHeader';
import { ProgressFill } from '../components/ProgressFill';
import {
  StatGrid,
  StatItem,
  StatSubText,
} from '../components/StatBlock';
import { ThirdPartyLogo } from '../components/ThirdPartyLogos';
import { useChartReady } from '../hooks/useChartReady';
import { getPersonName } from '../config/personNames';
import { getCategoryIcon } from '../components/CategoryIcon';
import clsx from 'clsx';

const TOOLTIP_STYLE = {
  background: 'var(--berg-bg-elevated)',
  border: '1px solid var(--berg-border-strong)',
  borderRadius: 12,
  fontSize: 12,
  color: 'var(--berg-text)',
  boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
};
const TOOLTIP_LABEL_STYLE = { color: 'var(--berg-text)', fontWeight: 600 };
const TOOLTIP_ITEM_STYLE = { color: 'var(--berg-text)' };

function fmt0(n: number) {
  return formatCurrency(n, 'USD', 'en-US', 0);
}

function fmtCents(n: number) {
  return formatCurrency(n);
}

function pickScope(t: Tri, scope: Scope): number {
  return t[scope];
}

function pct(n: number) {
  return `${(n * 100).toFixed(2)}%`;
}

// DOM ids for deep-linking from the Net Worth tab. Stripping the emoji + any
// non-alphanumeric characters keeps the slug stable even if a category's
// emoji changes in the sheet.
function categoryDomId(rawName: string): string {
  const bare = splitCategoryEmoji(rawName).name;
  const slug = bare
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return `asset-cat-${slug}`;
}

const LIQUID_CATEGORY = /emergency|cash|sinking|crypto|invest|stock|fund|equity|etf/i;
const LONG_TERM_CATEGORY = /retirement|long\s?term/i;

type Liquidity = 'liquid' | 'longTerm' | 'fixed';

function classifyLiquidity(name: string): Liquidity {
  // Private holdings are illiquid even though they're stocks.
  if (/private/i.test(name)) return 'fixed';
  if (LIQUID_CATEGORY.test(name)) return 'liquid';
  if (LONG_TERM_CATEGORY.test(name)) return 'longTerm';
  return 'fixed';
}

function addTri(a: Tri, b: Tri): Tri {
  return {
    household: a.household + b.household,
    dakota: a.dakota + b.dakota,
    natalia: a.natalia + b.natalia,
  };
}

function PortfolioRow({
  label,
  data,
  returnPct,
}: {
  label: string;
  data: { cost: number; value: number; profit: number };
  returnPct: number;
}) {
  const empty = data.value === 0 && data.cost === 0 && data.profit === 0;
  if (empty) {
    return (
      <StatGrid columns={2} gap={6} className="border-t border-border pt-4">
        <StatItem
          label={label}
          valueSize="xl"
          value={<span className="text-fg-tertiary">—</span>}
          sub={<StatSubText unit="No holdings" />}
        />
      </StatGrid>
    );
  }
  const positive = data.profit >= 0;
  return (
    <StatGrid columns={2} gap={6} className="border-t border-border pt-4">
      <StatItem
        label={label}
        valueSize="xl"
        value={fmt0(data.value)}
        sub={
          data.cost > 0 ? (
            <StatSubText amount={fmt0(data.cost)} unit="invested" />
          ) : (
            <StatSubText unit="No cost basis" />
          )
        }
      />
      <StatItem
        label={`${label} P/L`}
        valueSize="xl"
        valueTone={positive ? 'pos' : 'neg'}
        value={`${positive ? '+' : ''}${fmt0(data.profit)}`}
        sub={
          data.cost > 0 ? (
            <StatSubText
              amount={`${returnPct >= 0 ? '+' : ''}${returnPct.toFixed(1)}%`}
              unit="return"
            />
          ) : (
            <StatSubText amount="—" />
          )
        }
      />
    </StatGrid>
  );
}

function HeroAssets({
  assets,
  investments,
  scope,
}: {
  assets: AssetsData;
  investments: InvestmentsData;
  scope: Scope;
}) {
  // Sum categories rather than trusting the sheet's "Total Assets" row, which
  // can drift from the category sum if a formula gets out of sync. Keeps the
  // hero number aligned with the donut's slices.
  const totalBalance: Tri = [
    ...assets.categories.map((c) => c.balance),
    ...investments.categories.map((c) => c.value),
  ].reduce(addTri, { household: 0, dakota: 0, natalia: 0 });
  const balance = pickScope(totalBalance, scope);
  const annual = pickScope(assets.totals.annualEarnings, scope);
  const monthly = pickScope(assets.totals.monthlyEarnings, scope);

  // Investment portfolio buckets — split into Stocks (incl. funds & private)
  // vs Crypto so we can show how each asset class is performing.
  const isCrypto = (name: string) => /crypto|bitcoin/i.test(name);
  const aggregate = (predicate: (n: string) => boolean) =>
    investments.categories
      .filter((c) => predicate(c.name))
      .reduce(
        (acc, c) => ({
          cost: acc.cost + pickScope(c.invested, scope),
          value: acc.value + pickScope(c.value, scope),
          profit: acc.profit + pickScope(c.profit, scope),
        }),
        { cost: 0, value: 0, profit: 0 }
      );
  const stocks = aggregate((n) => !isCrypto(n));
  const crypto = aggregate(isCrypto);

  function asReturn(p: { cost: number; profit: number }) {
    return p.cost > 0 ? (p.profit / p.cost) * 100 : 0;
  }

  // Cross-element hover sync between the liquidity bar and its legend.
  const [hoveredBand, setHoveredBand] = useState<string | null>(null);

  // Bucket asset + investment categories into liquidity classes.
  const buckets: Record<Liquidity, number> = { liquid: 0, longTerm: 0, fixed: 0 };
  for (const c of assets.categories) {
    buckets[classifyLiquidity(c.name)] += pickScope(c.balance, scope);
  }
  for (const c of investments.categories) {
    buckets[classifyLiquidity(c.name)] += pickScope(c.value, scope);
  }
  const cashShare = balance > 0 ? (buckets.liquid / balance) * 100 : 0;

  // Colors come from the shared CATEGORY_COLORS map (via getCategoryColor)
  // so every segmented bar in the app reaches for one source of truth.
  const liquidityBands = [
    { key: 'liquid' as const, label: 'Liquid', value: buckets.liquid, color: getCategoryColor('Liquid') },
    {
      key: 'longTerm' as const,
      label: 'Long-term',
      value: buckets.longTerm,
      color: getCategoryColor('Long-term'),
    },
    {
      key: 'fixed' as const,
      label: 'Fixed',
      value: buckets.fixed,
      color: getCategoryColor('Fixed'),
    },
  ];

  return (
    <div className="card flex flex-col gap-5 h-full">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div className="flex flex-col gap-1">
          <span className="hero-eyebrow">
            {scope === 'household'
              ? 'Total assets'
              : `${getPersonName(scope)} assets`}
          </span>
          <div className="hero-balance tabular leading-none">{fmt0(balance)}</div>
        </div>
        {scope === 'household' && (
          <PersonBreakdown
            dakota={totalBalance.dakota}
            natalia={totalBalance.natalia}
            digits={0}
          />
        )}
      </div>

      <div className="flex flex-col gap-3 border-t border-border pt-4">
        <div className="flex items-center justify-between">
          <span className="stat-label">Liquidity</span>
          <span className="text-xs text-fg-tertiary tabular">
            {cashShare.toFixed(0)}% liquid
          </span>
        </div>
        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-subtle gap-0.5">
          {liquidityBands.map((b) => {
            const pct = balance > 0 ? (b.value / balance) * 100 : 0;
            const dimmed = hoveredBand !== null && hoveredBand !== b.key;
            return (
              <ProgressFill
                key={b.key}
                pct={pct}
                color={b.color}
                style={{ opacity: dimmed ? 0.2 : 1 }}
                onMouseEnter={() => setHoveredBand(b.key)}
                onMouseLeave={() => setHoveredBand(null)}
              />
            );
          })}
        </div>
        <div className="hidden sm:block">
          <BreakdownLegendGrid
            items={liquidityBands.map((b) => ({
              key: b.key,
              label: b.label,
              value: b.value,
              color: b.color,
            }))}
            total={balance}
            formatValue={fmt0}
            hovered={hoveredBand}
            onHoverChange={setHoveredBand}
          />
        </div>
        <div className="sm:hidden">
          <BreakdownLegend
            items={liquidityBands.map((b) => ({
              key: b.key,
              label: b.label,
              value: b.value,
              color: b.color,
            }))}
            total={balance}
            formatValue={fmt0}
            hovered={hoveredBand}
            onHoverChange={setHoveredBand}
            collapseAfter={Infinity}
          />
        </div>
      </div>

      <StatGrid columns={2} gap={6} className="border-t border-border pt-4">
        <StatItem
          label="Monthly earnings"
          valueSize="xl"
          valueTone="pos"
          value={`+${fmtCents(monthly)}`}
          sub={<StatSubText unit="from interest" />}
        />
        <StatItem
          label="Annual earnings"
          valueSize="xl"
          valueTone="pos"
          value={`+${fmt0(annual)}`}
          sub={<StatSubText unit="from interest" />}
        />
      </StatGrid>

      <PortfolioRow label="Stocks" data={stocks} returnPct={asReturn(stocks)} />
      <PortfolioRow label="Crypto" data={crypto} returnPct={asReturn(crypto)} />
    </div>
  );
}

function AssetBreakdownCard({
  assets,
  investments,
  scope,
}: {
  assets: AssetsData;
  investments: InvestmentsData;
  scope: Scope;
}) {
  const allCategoryNames = useMemo(
    () => [
      ...assets.categories.map((c) => c.name),
      ...investments.categories.map((c) => c.name),
    ],
    [assets.categories, investments.categories]
  );
  const colorMap = useMemo(
    () => assignCategoryColors(allCategoryNames),
    [allCategoryNames]
  );

  const breakdown = useMemo(() => {
    const fromAssets = assets.categories.map((c) => ({
      label: c.name,
      value: pickScope(c.balance, scope),
      household: c.balance.household,
      color: colorMap.get(c.name) ?? 'var(--berg-neutral)',
    }));
    const fromInvestments = investments.categories.map((c) => ({
      label: c.name,
      value: pickScope(c.value, scope),
      household: c.value.household,
      color: colorMap.get(c.name) ?? 'var(--berg-neutral)',
    }));
    // Keep zero-value categories in the legend so the key always lists every
    // category, but sort by the active scope's value so the legend reads
    // largest-first for whichever person is being viewed.
    return [...fromAssets, ...fromInvestments].sort(
      (a, b) => b.value - a.value,
    );
  }, [assets.categories, investments.categories, scope, colorMap]);

  const pieData = useMemo(() => breakdown.filter((b) => b.value > 0), [breakdown]);
  const total = breakdown.reduce((sum, b) => sum + b.value, 0);
  const [hovered, setHoveredRaw] = useState<string | null>(null);
  // Don't accept hovers until the entry animation has run — mid-mount
  // hover would change Cell fillOpacity and freeze the swoop-in.
  const ready = useChartReady();
  const setHovered = (next: string | null) => {
    if (ready) setHoveredRaw(next);
  };

  return (
    <div className="card">
      <CardHeader
        title="Breakdown"
        subtitle="By category"
        right={<span className="tabular text-2xl font-semibold">{fmt0(total)}</span>}
      />
      {pieData.length === 0 ? (
        <div className="empty-state py-8">No assets in this scope.</div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="relative h-[220px]">
            <DonutCenterEmoji
              emoji={hovered ? splitCategoryEmoji(hovered).emoji : null}
            />
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  animationDuration={1000}
                  nameKey="label"
                  innerRadius="60%"
                  outerRadius="98%"
                  startAngle={90}
                  endAngle={-270}
                  paddingAngle={0.5}
                  stroke="var(--berg-bg-elevated)"
                  strokeWidth={1}
                  onMouseEnter={(_, idx) => setHovered(pieData[idx]?.label ?? null)}
                  onMouseLeave={() => setHovered(null)}
                >
                  {pieData.map((b) => (
                    <Cell
                      key={b.label}
                      fill={b.color}
                      fillOpacity={hovered === null || hovered === b.label ? 1 : 0.2}
                      style={{ transition: 'fill-opacity 0.18s ease' }}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v) => fmtCents(typeof v === 'number' ? v : 0)}
                  contentStyle={TOOLTIP_STYLE}
                  labelStyle={TOOLTIP_LABEL_STYLE}
                  itemStyle={TOOLTIP_ITEM_STYLE}
                  cursor={{ fill: 'transparent' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <BreakdownLegend
            items={breakdown.map((b) => ({
              key: b.label,
              label: stripCategoryEmoji(b.label),
              value: b.value,
              color: b.color,
            }))}
            total={total}
            formatValue={fmt0}
            hovered={hovered}
            onHoverChange={setHovered}
          />
        </div>
      )}
    </div>
  );
}

function AssetCategoryCard({
  category,
  scope,
  totalBalance,
  expanded,
  onToggle,
  domId,
}: {
  category: AssetCategory;
  scope: Scope;
  totalBalance: number;
  expanded: boolean;
  onToggle: () => void;
  domId?: string;
}) {
  const { emoji, name: bareName } = splitCategoryEmoji(category.name);
  const Icon = emoji ? null : getCategoryIcon(category.name);
  const balance = pickScope(category.balance, scope);
  const monthly = pickScope(category.monthlyEarnings, scope);
  const share = totalBalance > 0 ? (balance / totalBalance) * 100 : 0;

  // Hide items that are zero in the current scope.
  const visibleItems = category.items.filter((i) => pickScope(i.balance, scope) !== 0);

  return (
    <div id={domId} className="card-flush scroll-mt-20">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 px-5 py-4 max-md:px-4 text-left hover:bg-overlay transition-colors"
      >
        {emoji ? (
          <span aria-hidden className="w-8 flex-none text-center text-2xl leading-none">
            {emoji}
          </span>
        ) : (
          Icon && (
            <Icon
              size={22}
              weight="duotone"
              className="text-fg-secondary flex-none"
            />
          )
        )}
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{bareName}</div>
          <div className="text-xs text-fg-tertiary mt-0.5">
            {visibleItems.length} {visibleItems.length === 1 ? 'asset' : 'assets'} ·{' '}
            {share.toFixed(1)}%
          </div>
        </div>
        <div className="flex items-center gap-3 flex-none">
          <div className="flex flex-col items-end gap-0.5">
            <span className="tabular font-semibold text-base">{fmt0(balance)}</span>
            {monthly > 0 && (
              <span className="text-xs delta-pos tabular">
                +{fmtCents(monthly)}/mo
              </span>
            )}
          </div>
          <CaretDownIcon
            size={16}
            weight="bold"
            className={clsx(
              'text-fg-tertiary transition-transform',
              expanded && 'rotate-180'
            )}
          />
        </div>
      </button>

      {expanded && visibleItems.length > 0 && (
        <div className="border-t border-border">
          <div className="grid grid-cols-[1fr_4rem_6rem_6rem] gap-x-4 px-5 py-2 border-b border-border text-[0.6875rem] uppercase tracking-[0.08em] font-medium text-fg-tertiary max-md:hidden">
            <div className="pl-[48px]">Asset</div>
            <div className="text-right">APY</div>
            <div className="text-right">Earning/mo</div>
            <div className="text-right">Balance</div>
          </div>
          {visibleItems.map((item) => (
            <AssetItemRow key={item.name} item={item} scope={scope} />
          ))}
        </div>
      )}
    </div>
  );
}

function AssetItemRow({ item, scope }: { item: AssetItem; scope: Scope }) {
  const balance = pickScope(item.balance, scope);
  const monthly = pickScope(item.monthlyEarnings, scope);
  return (
    <>
      <div className="grid grid-cols-[1fr_4rem_6rem_6rem] gap-x-4 items-center px-5 py-2.5 border-b border-border last:border-b-0 hover:bg-overlay transition-colors max-md:hidden">
        <div className="flex items-center gap-4 text-sm text-fg-secondary truncate">
          <span className="w-8 flex-none flex items-center justify-center">
            <ThirdPartyLogo location={item.location} size={20} />
          </span>
          <span className="truncate">{item.name}</span>
        </div>
        <div className="text-right tabular text-sm text-fg-tertiary">
          {item.annualRate > 0 ? pct(item.annualRate) : '—'}
        </div>
        <div className="text-right tabular text-sm">
          {monthly > 0 ? (
            <span className="delta-pos">+{fmtCents(monthly)}</span>
          ) : (
            <span className="text-fg-tertiary">—</span>
          )}
        </div>
        <div className="text-right tabular text-sm font-medium">{fmt0(balance)}</div>
      </div>
      <div className="hidden max-md:flex flex-col gap-1 px-4 py-2.5 border-b border-border last:border-b-0">
        <div className="flex items-center gap-4 text-sm">
          <span className="w-8 flex-none flex items-center justify-center">
            <ThirdPartyLogo location={item.location} size={20} />
          </span>
          <span className="text-fg-secondary truncate flex-1 min-w-0">
            {item.name}
          </span>
          <span className="tabular font-medium shrink-0">{fmt0(balance)}</span>
        </div>
        {(item.annualRate > 0 || monthly > 0) && (
          <div className="flex items-baseline justify-between gap-3 pl-[48px] text-xs tabular">
            <span className="text-fg-tertiary">
              {item.annualRate > 0 ? `${pct(item.annualRate)} APY` : ''}
            </span>
            {monthly > 0 && (
              <span className="delta-pos shrink-0">+{fmtCents(monthly)}/mo</span>
            )}
          </div>
        )}
      </div>
    </>
  );
}


function InvestmentCategoryCard({
  category,
  scope,
  totalBalance,
  expanded,
  onToggle,
  domId,
}: {
  category: InvestmentCategory;
  scope: Scope;
  totalBalance: number;
  expanded: boolean;
  onToggle: () => void;
  domId?: string;
}) {
  const { emoji, name: bareName } = splitCategoryEmoji(category.name);
  const Icon = emoji ? null : getCategoryIcon(category.name);
  const value = pickScope(category.value, scope);
  const invested = pickScope(category.invested, scope);
  const profit = pickScope(category.profit, scope);
  const changePct = invested > 0 ? (profit / invested) * 100 : 0;
  const positive = profit >= 0;
  const share = totalBalance > 0 ? (value / totalBalance) * 100 : 0;
  const visibleItems = category.items.filter((i) => pickScope(i.value, scope) > 0);
  const kind = categoryKind(category.name);

  return (
    <div id={domId} className="card-flush scroll-mt-20">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 px-5 py-4 max-md:px-4 text-left hover:bg-overlay transition-colors"
      >
        {emoji ? (
          <span aria-hidden className="w-8 flex-none text-center text-2xl leading-none">
            {emoji}
          </span>
        ) : (
          Icon && (
            <Icon
              size={22}
              weight="duotone"
              className="text-fg-secondary flex-none"
            />
          )
        )}
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{bareName}</div>
          <div className="text-xs text-fg-tertiary mt-0.5">
            {visibleItems.length} {visibleItems.length === 1 ? 'holding' : 'holdings'} ·{' '}
            {share.toFixed(1)}%
          </div>
        </div>
        <div className="flex items-center gap-3 flex-none">
          <div className="flex flex-col items-end gap-0.5">
            <span className="tabular font-semibold text-base">{fmt0(value)}</span>
            {invested > 0 && (
              <span
                className={clsx(
                  'text-xs tabular',
                  positive ? 'delta-pos' : 'delta-neg',
                )}
              >
                {positive ? '+' : ''}
                {fmt0(profit)} ({changePct.toFixed(1)}%)
              </span>
            )}
          </div>
          <CaretDownIcon
            size={16}
            weight="bold"
            className={clsx(
              'text-fg-tertiary transition-transform',
              expanded && 'rotate-180'
            )}
          />
        </div>
      </button>

      {expanded && visibleItems.length > 0 && (
        <div className="border-t border-border">
          <div
            className={clsx(
              'grid gap-x-4 px-5 py-2 border-b border-border text-[0.6875rem] uppercase tracking-[0.08em] font-medium text-fg-tertiary max-md:hidden',
              kind !== 'private'
                ? 'grid-cols-[7rem_1fr_6rem_5rem_8rem_6rem]'
                : 'grid-cols-[7rem_1fr_5rem_8rem_6rem]',
            )}
          >
            <div className="pl-[48px]">Ticker</div>
            <div>Name</div>
            {kind !== 'private' && <div className="text-right">Position</div>}
            <div className="text-right">Cost</div>
            <div className="text-right">P/L</div>
            <div className="text-right">Value</div>
          </div>
          {visibleItems.map((item) => (
            <InvestmentItemRow
              key={`${item.ticker}-${item.name}`}
              item={item}
              scope={scope}
              categoryKind={kind}
            />
          ))}
        </div>
      )}
    </div>
  );
}

type CategoryKind = 'crypto' | 'private' | 'tradable';

function categoryKind(name: string): CategoryKind {
  const n = name.toLowerCase();
  if (n.includes('crypto')) return 'crypto';
  if (n.includes('private')) return 'private';
  return 'tradable';
}

function formatPosition(n: number): string {
  if (n === 0) return '0';
  // For tiny fractional positions (typical of crypto), show more precision.
  // For whole shares like 5000 DOGE, default formatting is fine.
  if (Math.abs(n) >= 1) return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
  if (Math.abs(n) >= 0.01) return n.toFixed(4);
  if (Math.abs(n) >= 0.0001) return n.toFixed(6);
  return n.toExponential(2);
}

function InvestmentItemRow({
  item,
  scope,
  categoryKind,
}: {
  item: Investment;
  scope: Scope;
  categoryKind: CategoryKind;
}) {
  const value = pickScope(item.value, scope);
  const invested = pickScope(item.invested, scope);
  const profit = pickScope(item.profit, scope);
  const position = pickScope(item.position, scope);
  const changePct = invested > 0 ? (profit / invested) * 100 : 0;
  const showProfit = invested > 0 || profit !== 0;
  const positive = profit >= 0;
  const showPosition = categoryKind !== 'private';
  const positionLabel = showPosition && position > 0 ? formatPosition(position) : null;

  return (
    <>
      <div
        className={clsx(
          'grid gap-x-4 items-center px-5 py-2.5 border-b border-border last:border-b-0 hover:bg-overlay transition-colors max-md:hidden',
          showPosition
            ? 'grid-cols-[7rem_1fr_6rem_5rem_8rem_6rem]'
            : 'grid-cols-[7rem_1fr_5rem_8rem_6rem]',
        )}
      >
        <div className="flex items-center gap-4 text-sm font-semibold tabular truncate">
          <span className="w-8 flex-none flex items-center justify-center">
            <ThirdPartyLogo location={item.location} size={20} />
          </span>
          <span className="truncate">{item.ticker || '—'}</span>
        </div>
        <div className="text-sm text-fg-secondary truncate min-w-0">{item.name}</div>
        {showPosition && (
          <div className="text-right tabular text-sm text-fg-tertiary">
            {positionLabel ?? '—'}
          </div>
        )}
        <div className="text-right tabular text-sm text-fg-tertiary">
          {invested > 0 ? fmt0(invested) : '—'}
        </div>
        <div className="text-right tabular text-sm">
          {showProfit ? (
            <span className={clsx(positive ? 'delta-pos' : 'delta-neg')}>
              {positive ? '+' : ''}
              {fmt0(profit)}
              {invested > 0 && (
                <span className="text-fg-tertiary ml-1">
                  ({changePct.toFixed(0)}%)
                </span>
              )}
            </span>
          ) : (
            <span className="text-fg-tertiary">—</span>
          )}
        </div>
        <div className="text-right tabular text-sm font-medium">{fmt0(value)}</div>
      </div>
      <div className="hidden max-md:flex flex-col gap-1 px-4 py-2.5 border-b border-border last:border-b-0">
        <div className="flex items-center gap-4 text-sm">
          <span className="w-8 flex-none flex items-center justify-center">
            <ThirdPartyLogo location={item.location} size={20} />
          </span>
          <span className="font-semibold tabular flex-none whitespace-nowrap">
            {item.ticker || '—'}
          </span>
          <span className="text-fg-secondary truncate flex-1 min-w-0">
            {item.name}
          </span>
          <span className="tabular font-medium shrink-0">{fmt0(value)}</span>
        </div>
        {(showProfit || positionLabel || invested > 0) && (
          <div className="flex items-baseline justify-between gap-3 pl-[48px] text-xs tabular">
            <span className="text-fg-tertiary truncate">
              {positionLabel
                ? positionLabel
                : invested > 0
                  ? `${fmt0(invested)} cost`
                  : ''}
            </span>
            {showProfit && (
              <span
                className={clsx('shrink-0', positive ? 'delta-pos' : 'delta-neg')}
              >
                {positive ? '+' : ''}
                {fmt0(profit)}
                {invested > 0 && (
                  <span className="text-fg-tertiary ml-1">
                    ({changePct.toFixed(0)}%)
                  </span>
                )}
              </span>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export function AssetsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { data, isLoading, error } = useDashboard();
  const { scope } = useScope();
  const [expandedSet, setExpandedSet] = useState<Set<string>>(new Set());
  const [searchParams, setSearchParams] = useSearchParams();
  const catParam = searchParams.get('cat');

  const assets = data?.assets;
  const investments = data?.investments;

  type UnifiedCategory =
    | { kind: 'asset'; data: AssetCategory; balance: number }
    | { kind: 'investment'; data: InvestmentCategory; balance: number };

  const visibleCategories = useMemo<UnifiedCategory[]>(() => {
    if (!assets || !investments) return [];
    const list: UnifiedCategory[] = [
      ...assets.categories
        .filter((c) => c.items.some((i) => pickScope(i.balance, scope) !== 0))
        .map((c) => ({
          kind: 'asset' as const,
          data: c,
          balance: pickScope(c.balance, scope),
        })),
      ...investments.categories
        .filter((c) => c.items.some((i) => pickScope(i.value, scope) > 0))
        .map((c) => ({
          kind: 'investment' as const,
          data: c,
          balance: pickScope(c.value, scope),
        })),
    ];
    return list.sort((a, b) => b.balance - a.balance);
  }, [assets, investments, scope]);

  const toggle = (name: string) =>
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const totalBalance =
    assets && investments
      ? [...assets.categories.map((c) => c.balance), ...investments.categories.map((c) => c.value)]
          .reduce((sum, t) => sum + pickScope(t, scope), 0)
      : 0;

  // Deep-link from the Net Worth tab: when ?cat= matches a category's bare
  // name, expand it, scroll it into view, then drop the param so a manual
  // collapse later actually sticks.
  useEffect(() => {
    if (!catParam || visibleCategories.length === 0) return;
    const want = catParam.toLowerCase();
    const target = visibleCategories.find(
      (c) => splitCategoryEmoji(c.data.name).name.toLowerCase() === want,
    );
    if (!target) return;
    setExpandedSet((prev) => {
      if (prev.has(target.data.name)) return prev;
      const next = new Set(prev);
      next.add(target.data.name);
      return next;
    });
    requestAnimationFrame(() => {
      document
        .getElementById(categoryDomId(target.data.name))
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('cat');
        return next;
      },
      { replace: true },
    );
  }, [catParam, visibleCategories, setSearchParams]);

  const body = (
    <>
      {error && <div className="alert-error">{error}</div>}

      {!data && isLoading && (
        <div className="card text-center py-12 text-fg-secondary">Loading…</div>
      )}

      {assets && investments && (
        <>
          <div className="grid grid-cols-3 gap-5 max-md:grid-cols-1">
            <div className="col-span-2 max-md:col-span-1">
              <HeroAssets assets={assets} investments={investments} scope={scope} />
            </div>
            <AssetBreakdownCard assets={assets} investments={investments} scope={scope} />
          </div>

          {visibleCategories.length === 0 ? (
            <div className="card empty-state">
              No assets recorded for{' '}
              {scope === 'household' ? 'this household' : getPersonName(scope)}
              .
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {visibleCategories.map((c) =>
                c.kind === 'asset' ? (
                  <AssetCategoryCard
                    key={c.data.name}
                    category={c.data}
                    scope={scope}
                    totalBalance={totalBalance}
                    expanded={expandedSet.has(c.data.name)}
                    onToggle={() => toggle(c.data.name)}
                    domId={categoryDomId(c.data.name)}
                  />
                ) : (
                  <InvestmentCategoryCard
                    key={c.data.name}
                    category={c.data}
                    scope={scope}
                    totalBalance={totalBalance}
                    expanded={expandedSet.has(c.data.name)}
                    onToggle={() => toggle(c.data.name)}
                    domId={categoryDomId(c.data.name)}
                  />
                )
              )}
            </div>
          )}
        </>
      )}
    </>
  );

  // Embedded inside NetWorthPage's tabs: caller already provides the page
  // wrapper and header, so render the body bare and rely on the parent's
  // `space-y-5` for vertical rhythm between the embedded sections.
  if (embedded) {
    return <div className="space-y-5">{body}</div>;
  }

  return (
    <div className="page-container space-y-5">
      <header className="page-header">
        <div>
          <h1 className="page-title">Assets</h1>
          <p className="page-subtitle">Where your money lives and what it's earning</p>
        </div>
      </header>
      {body}
    </div>
  );
}
